import { PackageData } from "@/pages/CreatePackage";
import { TemplateStyle } from "../types";
import { companyName } from "../constants";
import { generateQrCode } from "../helpers";
import { renderCommonSection } from "../commonSections";

const renderSection = (sectionId: string, packageData: PackageData, colors: { [key: string]: string }): string => {
  const common = renderCommonSection(sectionId, packageData);
  if (common !== null) return common;

  const gold = colors.accent || '#d4a853';

  switch (sectionId) {
    case 'client': {
      const logoHtml = packageData.companyLogoUrl 
        ? `<img src="${packageData.companyLogoUrl}" alt="شعار الشركة" style="max-height:50px;border-radius:8px;margin-left:15px;vertical-align:middle;" />` : '';
      const companyHtml = packageData.companyName 
        ? `<div style="margin-bottom:12px;display:flex;align-items:center;">${logoHtml}<span style="font-size:16px;font-weight:bold;color:${gold};">${packageData.companyName}</span></div>` : '';
      
      return `<div class="t7-section">
        <div class="t7-section-title"><span class="t7-icon">👤</span> معلومات العميل</div>
        <div class="t7-section-body">
          ${companyHtml}
          <div class="t7-grid">
            <div class="t7-info-item"><span class="t7-label">الاسم</span><span class="t7-value">${packageData.clientName}</span></div>
            <div class="t7-info-item"><span class="t7-label">البريد</span><span class="t7-value">${packageData.clientEmail || '—'}</span></div>
            <div class="t7-info-item"><span class="t7-label">الهاتف</span><span class="t7-value">${packageData.clientPhone || '—'}</span></div>
            <div class="t7-info-item"><span class="t7-label">النوع</span><span class="t7-value">${packageData.clientType === 'b2b' ? 'شراكة تجارية' : 'عميل فردي'}</span></div>
            <div class="t7-info-item"><span class="t7-label">العنوان</span><span class="t7-value">${packageData.clientAddress || '—'}</span></div>
            <div class="t7-info-item"><span class="t7-label">عدد البالغين</span><span class="t7-value">${packageData.adultsCount || 0}</span></div>
            <div class="t7-info-item"><span class="t7-label">عدد الأطفال</span><span class="t7-value">${packageData.childrenCount || 0}</span></div>
            <div class="t7-info-item"><span class="t7-label">عدد الرضع</span><span class="t7-value">${packageData.infantsCount || 0}</span></div>
          </div>
        </div>
      </div>`;
    }

    case 'hotels':
      if (!packageData.hotels || packageData.hotels.length === 0) return '';
      return `<div class="t7-section">
        <div class="t7-section-title"><span class="t7-icon">🏨</span> الفنادق والإقامة</div>
        <div class="t7-section-body">
          ${packageData.hotels.map(hotel => `
            <div class="t7-card">
              <div class="t7-card-header">
                <span class="t7-card-title">${hotel.name}</span>
                <span class="t7-badge">${hotel.nights} ليالي</span>
              </div>
              <div class="t7-card-details">
                <div class="t7-detail"><span>📍</span> ${hotel.location}</div>
                <div class="t7-detail"><span>🛏️</span> ${hotel.roomType || 'غرفة قياسية'}</div>
                <div class="t7-detail"><span>🍽️</span> ${(packageData.mealPlans || []).join(' + ') || 'غير محدد'}</div>
                <div class="t7-detail"><span>🔢</span> ${packageData.numberOfRooms || 1} غرفة</div>
              </div>
              <div class="t7-card-footer">
                <span>📅 ${hotel.checkIn} → ${hotel.checkOut}</span>
                <span class="t7-price-tag">$${hotel.pricePerNight * hotel.nights}</span>
              </div>
            </div>
          `).join('')}
        </div>
      </div>`;

    case 'flights':
      if (!packageData.flights || packageData.flights.length === 0) return '';
      return `<div class="t7-section">
        <div class="t7-section-title"><span class="t7-icon">✈️</span> الرحلات الجوية</div>
        <div class="t7-section-body">
          ${packageData.flights.map(flight => `
            <div class="t7-card">
              <div class="t7-card-header">
                <span class="t7-card-title">${flight.airline || 'طيران'}</span>
                <span class="t7-badge ${flight.type === 'outbound' ? 'badge-blue' : flight.type === 'return' ? 'badge-green' : 'badge-amber'}">${flight.type === 'outbound' ? '🛫 ذهاب' : flight.type === 'return' ? '🛬 عودة' : '✈️ داخلي'}</span>
              </div>
              <div class="t7-flight-route">
                <div class="t7-airport">
                  <span class="t7-code">${flight.from}</span>
                  <span class="t7-city">${flight.fromCity || ''}</span>
                </div>
                <div class="t7-route-line">
                  <div class="t7-plane-icon">✈</div>
                  <div class="t7-line"></div>
                </div>
                <div class="t7-airport">
                  <span class="t7-code">${flight.to}</span>
                  <span class="t7-city">${flight.toCity || ''}</span>
                </div>
              </div>
              <div class="t7-card-details">
                <div class="t7-detail"><span>📅</span> ${flight.date}${flight.time ? ` - ${flight.time}` : ''}</div>
                <div class="t7-detail"><span>💺</span> ${flight.class || 'اقتصادي'}</div>
                <div class="t7-detail"><span>👥</span> ${(flight.adults || 0) + (flight.children || 0) + (flight.infants || 0)} مسافر</div>
              </div>
              ${flight.transitFlight ? `<div class="t7-transit">⏱️ رحلة ترانزيت: ${flight.transitDuration || '—'} ساعة</div>` : ''}
              <div class="t7-card-footer">
                <span>🧳 أمتعة: ${packageData.internationalBaggageCount || 1} × ${packageData.internationalBaggageWeight || 23}كجم</span>
                <span class="t7-price-tag">$${flight.price || 0}</span>
              </div>
            </div>
          `).join('')}
        </div>
      </div>`;

    case 'transport':
      if (!packageData.transport || packageData.transport.length === 0) return '';
      return `<div class="t7-section">
        <div class="t7-section-title"><span class="t7-icon">🚗</span> النقل والمواصلات</div>
        <div class="t7-section-body">
          <div class="t7-info-row">
            <div class="t7-info-item"><span class="t7-label">سيارات الاستقبال</span><span class="t7-value">${packageData.receptionCarsCount || 1}</span></div>
            <div class="t7-info-item"><span class="t7-label">سيارات الجولات</span><span class="t7-value">${packageData.tourCarsCount || 1}</span></div>
          </div>
          ${packageData.transport.map(item => `
            <div class="t7-card">
              <div class="t7-card-header">
                <span class="t7-card-title">${item.type === 'airport_transfer' ? '🚖 نقل مطار' : item.type === 'city_transport' ? '🚌 تنقل' : '🚙 جولة'}</span>
                <span class="t7-badge">${item.vehicle || 'سيارة خاصة'}</span>
              </div>
              <div class="t7-card-details">
                <div class="t7-detail"><span>📍</span> من: ${item.from} → إلى: ${item.to}</div>
                <div class="t7-detail"><span>📅</span> ${item.date || 'حسب الجدول'}</div>
              </div>
              <div class="t7-card-footer">
                <span></span>
                <span class="t7-price-tag">$${item.price || 0}</span>
              </div>
            </div>
          `).join('')}
        </div>
      </div>`;

    case 'tours':
      if (!packageData.tours || packageData.tours.length === 0) return '';
      return `<div class="t7-section">
        <div class="t7-section-title"><span class="t7-icon">🎯</span> الجولات السياحية</div>
        <div class="t7-section-body">
          ${packageData.tours.map(tour => `
            <div class="t7-card">
              <div class="t7-card-header">
                <span class="t7-card-title">${tour.name}</span>
              </div>
              ${tour.description ? `<p class="t7-description">${tour.description}</p>` : ''}
              ${tour.included && tour.included.length > 0 ? `
                <div class="t7-includes">
                  <strong>يشمل:</strong>
                  ${tour.included.map(i => `<span class="t7-include-item">✓ ${i}</span>`).join('')}
                </div>
              ` : ''}
              <div class="t7-card-footer">
                <span>📅 ${tour.date || 'حسب الجدول'} • ⏱️ ${tour.duration || 'يوم كامل'}</span>
                <span class="t7-price-tag">$${tour.price || 0}</span>
              </div>
            </div>
          `).join('')}
        </div>
      </div>`;

    case 'citiesPlan':
      if (!packageData.citiesPlan || packageData.citiesPlan.length === 0) return '';
      return `<div class="t7-section">
        <div class="t7-section-title"><span class="t7-icon">🗺️</span> خطة المدن</div>
        <div class="t7-section-body">
          <div class="t7-timeline">
            ${packageData.citiesPlan.map((city, idx) => `
              <div class="t7-timeline-item">
                <div class="t7-timeline-marker">${idx + 1}</div>
                <div class="t7-timeline-content">
                  <h4>${city.cityName || 'مدينة'}</h4>
                  <p>${city.nightsCount || 0} ليالي</p>
                </div>
              </div>
            `).join('')}
          </div>
        </div>
      </div>`;

    case 'price': {
      const basePrice = packageData.basePrice || packageData.totalPrice || 0;
      const discount = packageData.discount || 0;
      const taxes = packageData.taxes || 0;
      const total = packageData.totalPrice || 0;
      
      return `<div class="t7-section t7-price-section">
        <div class="t7-section-title"><span class="t7-icon">💰</span> تفاصيل الأسعار</div>
        <div class="t7-section-body">
          <table class="t7-price-table">
            <tr><td>السعر الأساسي</td><td class="t7-amount">$${basePrice}</td></tr>
            ${discount > 0 ? `<tr class="t7-discount"><td>الخصم</td><td class="t7-amount">-$${discount}</td></tr>` : ''}
            ${taxes > 0 ? `<tr><td>الضرائب والرسوم</td><td class="t7-amount">$${taxes}</td></tr>` : ''}
            <tr class="t7-total-row"><td>الإجمالي النهائي</td><td class="t7-amount t7-total-amount">$${total}</td></tr>
          </table>
          ${packageData.paymentTerms ? `<div class="t7-payment-terms"><strong>شروط الدفع:</strong> ${packageData.paymentTerms}</div>` : ''}
        </div>
      </div>`;
    }

    default:
      return '';
  }
};

export const getTemplate7HTML = async (packageData: PackageData, settings: TemplateStyle): Promise<string> => {
  const colors = settings.colors;
  const gold = colors.accent || '#d4a853';
  const primary = colors.primary || '#1a1a2e';
  const secondary = colors.secondary || '#16213e';
  const textColor = colors.text || '#f0e6d3';
  const bgColor = colors.background || '#0f0f23';

  const typo = settings.typography || { titleSize: 18, textSize: 13, fontWeight: 600, lineHeight: 1.6 };
  const sp = settings.spacing || { pageMarginTop: 30, pageMarginRight: 35, pageMarginBottom: 30, pageMarginLeft: 35, sectionGap: 25, sectionPadding: 20 };
  const bd = settings.borders || { width: 1, radius: 16, style: 'solid' };
  const hdr = settings.header || { showCover: true, coverHeight: 400 };
  const ftr = settings.footer || { showFooter: true, showQr: true, showPageNumber: true, customText: '' };
  const wm = settings.watermark || { text: '', opacity: 0.08, size: 60, rotation: -35 };
  const secStyles = settings.sectionStyles || {};

  const columnsHtml = settings.columns.map(column => {
    const sectionsHtml = column.sections.map(section => {
      const ss = secStyles[section.id];
      const sectionHtml = renderSection(section.id, packageData, colors);
      if (!sectionHtml || !ss) return sectionHtml;
      // Wrap with per-section style overrides
      return sectionHtml
        .replace('class="t7-section"', `class="t7-section" style="${ss.backgroundColor ? `background:${ss.backgroundColor};` : ''}${ss.borderColor ? `border-color:${ss.borderColor};` : ''}"`)
        .replace('class="t7-section-title"', `class="t7-section-title" style="${ss.titleColor ? `color:${ss.titleColor};` : ''}"`);
    }).join('');
    return `<div class="pdf-column">${sectionsHtml}</div>`;
  }).join('');

  const qrCodeData = await generateQrCode(`Package: ${packageData.packageName}\nClient: ${packageData.clientName}\nPrice: $${packageData.totalPrice}`);
  
  const logoHtml = settings.media?.logo 
    ? `<img src="${settings.media.logo}" alt="Logo" style="max-height:80px;margin:0 auto 20px;display:block;filter:drop-shadow(0 4px 8px rgba(0,0,0,0.3));" />` : '';
  
  const bgImage = settings.media?.background 
    ? `background-image: linear-gradient(180deg, rgba(15,15,35,0.85) 0%, rgba(26,26,46,0.95) 100%), url('${settings.media.background}'); background-size: cover; background-position: center;` : '';

  const watermarkHtml = wm.text ? `
    <div style="position:fixed;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:9999;display:flex;align-items:center;justify-content:center;">
      <span style="font-size:${wm.size}px;color:${gold};opacity:${wm.opacity};transform:rotate(${wm.rotation}deg);white-space:nowrap;font-weight:900;">${wm.text}</span>
    </div>` : '';

  const coverHtml = hdr.showCover ? `
    <div class="t7-cover" style="min-height:${hdr.coverHeight}px;">
      <div class="t7-cover-content">
        ${logoHtml}
        <div class="t7-gold-line"></div>
        <h1>${packageData.packageName || 'عرض سياحي فاخر'}</h1>
        <p class="t7-subtitle">${packageData.destination || ''} ${packageData.duration ? `• ${packageData.duration}` : ''}</p>
        <div class="t7-gold-line"></div>
        <p class="t7-company">${companyName}</p>
        <div class="t7-meta-strip">
          <div class="t7-meta-item"><span class="t7-meta-label">الوجهة</span><span class="t7-meta-value">${packageData.destination || '—'}</span></div>
          <div class="t7-meta-item"><span class="t7-meta-label">المدة</span><span class="t7-meta-value">${packageData.duration || '—'}</span></div>
          <div class="t7-meta-item"><span class="t7-meta-label">الغرف</span><span class="t7-meta-value">${packageData.numberOfRooms || 1}</span></div>
          <div class="t7-meta-item"><span class="t7-meta-label">المسافرون</span><span class="t7-meta-value">${(packageData.adultsCount || 0) + (packageData.childrenCount || 0)}</span></div>
        </div>
      </div>
    </div>` : '';

  const footerHtml = ftr.showFooter ? `
    <div class="t7-footer">
      <div class="t7-gold-line"></div>
      <div class="t7-footer-brand">${ftr.customText || companyName}</div>
      ${ftr.showQr ? `<img src="${qrCodeData}" alt="QR" style="width:80px;height:80px;margin:10px auto;display:block;border-radius:8px;border:2px solid ${gold}33;" />` : ''}
      <div class="t7-footer-date">
        تم إنشاء هذا العرض بتاريخ ${new Date().toLocaleDateString('ar-SA')}
        ${ftr.showPageNumber ? ' • صفحة 1' : ''}
      </div>
    </div>` : '';

  return `<!DOCTYPE html>
<html dir="rtl" lang="ar">
<head>
  <meta charset="UTF-8">
  <title>عرض سياحي - ${packageData.packageName}</title>
  <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;800;900&display=swap" rel="stylesheet">
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; font-family: 'Cairo', 'Arial', sans-serif; }
    
    body { background: ${bgColor}; color: ${textColor}; font-size: ${typo.textSize}px; font-weight: ${typo.fontWeight}; line-height: ${typo.lineHeight}; }
    
    .t7-container { max-width: 850px; margin: 0 auto; overflow: hidden; }
    
    .t7-cover {
      position: relative;
      display: flex; flex-direction: column; justify-content: center; align-items: center; text-align: center;
      padding: 60px 40px;
      background: linear-gradient(135deg, ${primary} 0%, ${secondary} 50%, ${primary} 100%);
      ${bgImage}
    }
    .t7-cover::before { content: ''; position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: linear-gradient(180deg, transparent 0%, ${primary}dd 100%); }
    .t7-cover-content { position: relative; z-index: 1; }
    .t7-gold-line { width: 120px; height: 3px; background: linear-gradient(90deg, transparent, ${gold}, transparent); margin: 20px auto; }
    .t7-cover h1 { font-size: 36px; font-weight: 900; color: ${gold}; text-shadow: 0 2px 20px ${gold}44; margin-bottom: 8px; }
    .t7-cover .t7-subtitle { font-size: 18px; color: ${textColor}; opacity: 0.9; letter-spacing: 1px; }
    .t7-cover .t7-company { font-size: 14px; color: ${gold}; opacity: 0.7; margin-top: 15px; letter-spacing: 2px; }
    .t7-meta-strip { display: flex; justify-content: center; gap: 30px; margin-top: 25px; padding: 15px 25px; background: ${primary}aa; border: 1px solid ${gold}33; border-radius: 12px; }
    .t7-meta-item { text-align: center; }
    .t7-meta-item .t7-meta-label { font-size: 11px; color: ${gold}; opacity: 0.7; display: block; }
    .t7-meta-item .t7-meta-value { font-size: 15px; font-weight: 700; color: ${textColor}; }
    
    .t7-body { padding: ${sp.pageMarginTop}px ${sp.pageMarginRight}px ${sp.pageMarginBottom}px ${sp.pageMarginLeft}px; background: ${bgColor}; }
    .pdf-row { display: flex; gap: 20px; }
    .pdf-column { flex: 1; min-width: 0; }
    
    .t7-section {
      margin-bottom: ${sp.sectionGap}px;
      background: linear-gradient(145deg, ${primary}ee, ${secondary}dd);
      border: ${bd.width}px ${bd.style} ${gold}22;
      border-radius: ${bd.radius}px;
      overflow: hidden;
      box-shadow: 0 4px 20px rgba(0,0,0,0.2);
    }
    .t7-section-title {
      padding: 14px 22px;
      font-size: ${typo.titleSize}px;
      font-weight: 800;
      color: ${gold};
      border-bottom: 1px solid ${gold}22;
      background: ${primary}88;
      display: flex; align-items: center; gap: 10px;
    }
    .t7-icon { font-size: ${typo.titleSize + 2}px; }
    .t7-section-body { padding: ${sp.sectionPadding}px 22px; }
    
    .t7-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
    .t7-info-item { padding: 10px 14px; background: ${bgColor}88; border-radius: ${Math.max(bd.radius - 6, 4)}px; border: 1px solid ${gold}11; }
    .t7-label { display: block; font-size: 11px; color: ${gold}; opacity: 0.7; margin-bottom: 4px; }
    .t7-value { display: block; font-size: ${typo.textSize + 1}px; font-weight: 600; color: ${textColor}; }
    .t7-info-row { display: flex; gap: 12px; margin-bottom: 15px; }
    .t7-info-row .t7-info-item { flex: 1; }
    
    .t7-card { margin-bottom: 15px; background: ${bgColor}66; border: 1px solid ${gold}15; border-radius: ${Math.max(bd.radius - 4, 4)}px; overflow: hidden; }
    .t7-card:last-child { margin-bottom: 0; }
    .t7-card-header { display: flex; justify-content: space-between; align-items: center; padding: 12px 16px; background: ${primary}66; border-bottom: 1px solid ${gold}11; }
    .t7-card-title { font-size: ${typo.textSize + 3}px; font-weight: 700; color: ${gold}; }
    .t7-badge { padding: 3px 12px; border-radius: 20px; font-size: 11px; font-weight: 600; background: ${gold}22; color: ${gold}; border: 1px solid ${gold}33; }
    .badge-blue { background: #3b82f622; color: #60a5fa; border-color: #3b82f633; }
    .badge-green { background: #22c55e22; color: #4ade80; border-color: #22c55e33; }
    .badge-amber { background: #f59e0b22; color: #fbbf24; border-color: #f59e0b33; }
    .t7-card-details { padding: 12px 16px; }
    .t7-detail { font-size: ${typo.textSize}px; color: ${textColor}cc; margin-bottom: 6px; display: flex; align-items: center; gap: 8px; }
    .t7-card-footer { display: flex; justify-content: space-between; align-items: center; padding: 10px 16px; border-top: 1px solid ${gold}11; font-size: 12px; color: ${textColor}aa; }
    .t7-price-tag { font-size: 16px; font-weight: 800; color: #4ade80; }
    .t7-description { padding: 8px 16px; font-size: ${typo.textSize}px; color: ${textColor}bb; line-height: ${typo.lineHeight}; }
    .t7-includes { padding: 10px 16px; background: ${gold}08; border-top: 1px solid ${gold}11; font-size: 12px; color: ${textColor}bb; }
    .t7-include-item { display: inline-block; margin: 2px 8px 2px 0; color: #4ade80; }
    .t7-transit { padding: 8px 16px; background: #f59e0b11; font-size: 12px; color: #fbbf24; }
    
    .t7-flight-route { display: flex; align-items: center; justify-content: center; padding: 15px 16px; gap: 15px; }
    .t7-airport { text-align: center; flex: 1; }
    .t7-code { display: block; font-size: 22px; font-weight: 900; color: ${gold}; }
    .t7-city { display: block; font-size: 11px; color: ${textColor}88; }
    .t7-route-line { flex: 0 0 80px; text-align: center; position: relative; }
    .t7-line { height: 2px; background: linear-gradient(90deg, ${gold}44, ${gold}, ${gold}44); margin: 8px 0; }
    .t7-plane-icon { font-size: 18px; color: ${gold}; }
    
    .t7-timeline { position: relative; padding-right: 30px; }
    .t7-timeline::before { content: ''; position: absolute; right: 12px; top: 0; bottom: 0; width: 2px; background: linear-gradient(180deg, ${gold}, ${gold}44); }
    .t7-timeline-item { position: relative; margin-bottom: 20px; padding-right: 15px; }
    .t7-timeline-marker { position: absolute; right: -30px; top: 0; width: 26px; height: 26px; border-radius: 50%; background: ${gold}; color: ${primary}; font-size: 12px; font-weight: 800; display: flex; align-items: center; justify-content: center; box-shadow: 0 0 12px ${gold}44; }
    .t7-timeline-content h4 { color: ${gold}; font-size: 15px; margin-bottom: 4px; }
    .t7-timeline-content p { font-size: ${typo.textSize}px; color: ${textColor}aa; }
    
    .t7-price-section { border: ${bd.width}px ${bd.style} ${gold}44; }
    .t7-price-table { width: 100%; border-collapse: collapse; }
    .t7-price-table td { padding: 12px 16px; font-size: ${typo.textSize + 1}px; }
    .t7-price-table td:first-child { color: ${textColor}cc; }
    .t7-amount { text-align: left; font-weight: 700; color: ${textColor}; }
    .t7-price-table tr { border-bottom: 1px solid ${gold}11; }
    .t7-discount td { color: #f87171 !important; }
    .t7-total-row { background: ${gold}11; }
    .t7-total-row td { font-size: 18px; font-weight: 800; color: ${gold} !important; }
    .t7-total-amount { color: #4ade80 !important; font-size: 22px !important; }
    .t7-payment-terms { padding: 12px 16px; font-size: ${typo.textSize}px; color: ${textColor}aa; border-top: 1px solid ${gold}11; }
    
    .section { margin-bottom: 15px; padding: 16px 20px; background: ${bgColor}66; border-radius: ${Math.max(bd.radius - 6, 4)}px; border: 1px solid ${gold}11; color: ${textColor}cc; font-size: ${typo.textSize}px; line-height: ${typo.lineHeight}; }
    .section h3 { color: ${gold}; font-size: ${typo.titleSize - 2}px; margin-bottom: 10px; font-weight: 700; }
    .section p, .section li { color: ${textColor}bb; }
    .section ul { padding-right: 20px; }
    .item { background: ${bgColor}44; padding: 12px; border-radius: 8px; border-right: 3px solid ${gold}44; margin-bottom: 8px; color: ${textColor}cc; }
    
    .t7-footer { text-align: center; padding: 30px 35px; background: linear-gradient(180deg, ${bgColor}, ${primary}); border-top: 1px solid ${gold}22; }
    .t7-footer-brand { font-size: 14px; color: ${gold}; opacity: 0.6; margin-bottom: 15px; letter-spacing: 1px; }
    .t7-footer-date { font-size: 12px; color: ${textColor}66; margin-top: 10px; }
    
    @media print {
      body { background: white; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .t7-section { break-inside: avoid; }
    }
  </style>
</head>
<body>
  ${watermarkHtml}
  <div class="t7-container">
    ${coverHtml}
    <div class="t7-body">
      <div class="pdf-row">
        ${columnsHtml}
      </div>
    </div>
    ${footerHtml}
  </div>
</body>
</html>`;
};
